import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Grid, Typography, Button, Box } from '@mui/material';

import Language from '../Language/Language';
import Panel from '../../Elements/Panel';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_TEXT_MUTED } from '../../constants/styleValues';

const componentContent = {
    language_DE: {
        heading: 'Wie funktioniert\'s?',
        intro: 'Multiplayer Quiz ist ein kostenloses Live-Quiz für dich und deine Freunde - direkt im Browser, ohne Anmeldung und ohne Download. In fünf Schritten seid ihr startklar.',
        steps: [
            { title: 'Namen wählen', text: 'Öffne die Startseite und gib deinen Namen ein - oder nutze einfach den witzigen Vorschlag, den wir dir automatisch anzeigen.' },
            { title: 'Quiz erstellen oder beitreten', text: 'Als Host erstellst du ein neues Quiz und bekommst eine PIN. Deine Freunde geben diese PIN einfach auf "Quiz beitreten" ein.' },
            { title: 'In der Lobby warten', text: 'Alle beigetretenen Spieler erscheinen live in der Lobby. Sobald alle da sind, startet der Host die Runde.' },
            { title: 'Live Fragen beantworten', text: 'Jede Frage hat ein Zeitlimit. Wählt eure Antwort, bevor die Zeit abläuft - je schneller und richtiger, desto mehr Punkte.' },
            { title: 'Punkte sammeln & gewinnen', text: 'Nach jeder Frage seht ihr die aktuelle Rangliste. Für Antwort-Serien gibt es Bonuspunkte. Am Ende gewinnt, wer die meisten Punkte hat.' }
        ],
        faqHeading: 'Häufige Fragen',
        faq: [
            { q: 'Ist Multiplayer Quiz kostenlos?', a: 'Ja, komplett kostenlos und ohne Registrierung - ihr braucht nur einen Namen.' },
            { q: 'Welches Gerät brauche ich?', a: 'Jedes Smartphone, Tablet oder Laptop mit Browser reicht - es muss nichts installiert werden.' },
            { q: 'In welchen Sprachen ist die App verfügbar?', a: 'Deutsch, Englisch und Französisch - umschaltbar über das Sprachmenü.' },
            { q: 'Wie werden die Punkte berechnet?', a: 'Ihr bekommt Punkte für richtige und schnelle Antworten. Wer mehrere Fragen hintereinander richtig beantwortet, sammelt zusätzliche Bonuspunkte für die Serie.' }
        ],
        cta: 'Jetzt kostenlos starten',
        backHome: 'Zurück zur Startseite',
        privacy: 'Datenschutzerklärung'
    },
    language_EN: {
        heading: 'How it works',
        intro: 'Multiplayer Quiz is a free live quiz for you and your friends - right in the browser, no signup and no download. Five steps and you\'re ready to play.',
        steps: [
            { title: 'Choose a name', text: 'Open the homepage and enter your name - or just keep the fun name we suggest automatically.' },
            { title: 'Create a quiz or join one', text: 'As host, create a new quiz and get a PIN. Your friends simply enter that PIN under "Join Quiz".' },
            { title: 'Wait in the lobby', text: 'Everyone who joins shows up live in the lobby. Once everyone is in, the host starts the round.' },
            { title: 'Answer questions live', text: 'Every question has a time limit. Pick your answer before time runs out - the faster and more accurate, the more points you get.' },
            { title: 'Score points & win', text: 'After each question you see the current leaderboard. Answer streaks earn bonus points. Whoever has the most points at the end wins.' }
        ],
        faqHeading: 'Frequently asked questions',
        faq: [
            { q: 'Is Multiplayer Quiz free?', a: 'Yes, completely free and no registration required - all you need is a name.' },
            { q: 'What device do I need?', a: 'Any smartphone, tablet or laptop with a browser works - nothing to install.' },
            { q: 'Which languages are supported?', a: 'German, English and French - switch anytime from the language menu.' },
            { q: 'How are points calculated?', a: 'You earn points for correct and fast answers. Answering several questions correctly in a row earns extra streak bonus points.' }
        ],
        cta: 'Start for free',
        backHome: 'Back to homepage',
        privacy: 'Privacy Statement'
    },
    language_FR: {
        heading: 'Comment ça marche',
        intro: 'Multiplayer Quiz est un quiz en direct gratuit pour toi et tes amis - directement dans le navigateur, sans inscription ni téléchargement. Cinq étapes et vous êtes prêts.',
        steps: [
            { title: 'Choisis un nom', text: 'Ouvre la page d\'accueil et entre ton nom - ou garde simplement le nom amusant que nous te proposons automatiquement.' },
            { title: 'Créer ou rejoindre un quiz', text: 'En tant qu\'hôte, crée un nouveau quiz et obtiens un code PIN. Tes amis entrent simplement ce PIN sous "Rejoindre un quiz".' },
            { title: 'Attendre dans le lobby', text: 'Tous les joueurs qui rejoignent apparaissent en direct dans le lobby. Une fois tout le monde présent, l\'hôte démarre la partie.' },
            { title: 'Répondre en direct', text: 'Chaque question a un temps limité. Choisissez votre réponse avant la fin du temps - plus vous êtes rapide et juste, plus vous gagnez de points.' },
            { title: 'Marquer des points & gagner', text: 'Après chaque question, vous voyez le classement actuel. Les séries de bonnes réponses rapportent des points bonus. À la fin, celui qui a le plus de points gagne.' }
        ],
        faqHeading: 'Questions fréquentes',
        faq: [
            { q: 'Multiplayer Quiz est-il gratuit ?', a: 'Oui, entièrement gratuit et sans inscription - il vous suffit d\'un nom.' },
            { q: 'De quel appareil ai-je besoin ?', a: 'N\'importe quel smartphone, tablette ou ordinateur portable avec un navigateur suffit - rien à installer.' },
            { q: 'Quelles langues sont disponibles ?', a: 'Allemand, anglais et français - changeable à tout moment depuis le menu des langues.' },
            { q: 'Comment les points sont-ils calculés ?', a: 'Vous gagnez des points pour des réponses justes et rapides. Enchaîner plusieurs bonnes réponses rapporte des points bonus supplémentaires.' }
        ],
        cta: 'Commencer gratuitement',
        backHome: 'Retour à l\'accueil',
        privacy: 'Privacy Statement'
    }
}

const HowItWorks = () => {
    const selectedLanguage = useSelector((state) => state.Language);
    const content = componentContent[selectedLanguage];

    return (
        <div>
            <Helmet>
                <title>How It Works - Multiplayer Quiz</title>
                <meta name="description" content="See how Multiplayer Quiz works: create a room, invite friends with a PIN, answer live questions and climb the leaderboard - free, no signup." />
                <link rel="canonical" href="https://multiplayer-quiz.com/how-it-works" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: componentContent.language_EN.faq.map((item) => ({
                            '@type': 'Question',
                            name: item.q,
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: item.a
                            }
                        }))
                    })}
                </script>
            </Helmet>

            <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pt: 3}}>
                <Language />
            </Grid>

            <Grid container justifyContent="center" textAlign="center" sx={{px: {xs: 2, sm: 3}, pb: 6}}>
                <Grid container maxWidth="md" justifyContent="center" textAlign="center" spacing={1}>

                    <Grid item xs={12} sx={{mt: 2, mb: 1}}>
                        <Typography variant="h1">{content.heading}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{mb: 4}}>
                        <Typography variant="body1" sx={{color: COLOR_TEXT_MUTED, maxWidth: '640px', margin: '0 auto'}}>
                            {content.intro}
                        </Typography>
                    </Grid>

                    {content.steps.map((step, index) => (
                        <Grid item xs={12} key={step.title} sx={{mb: 2}}>
                            <Panel sx={{p: 3, display: 'flex', gap: '16px', textAlign: 'left', alignItems: 'flex-start'}}>
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                    backgroundColor: COLOR_PRIMARY,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 700, fontSize: '18px', color: '#fff'
                                }}>
                                    {index + 1}
                                </Box>
                                <Box>
                                    <Typography variant="h3" sx={{mb: 0.5}}>{step.title}</Typography>
                                    <Typography variant="body2" sx={{color: COLOR_TEXT_MUTED}}>{step.text}</Typography>
                                </Box>
                            </Panel>
                        </Grid>
                    ))}

                    <Grid item xs={12} sx={{mt: 3, mb: 5}}>
                        <Button variant="contained" size="large" href="/">{content.cta}</Button>
                    </Grid>

                    <Grid item xs={12} sx={{mb: 2}}>
                        <Typography variant="h2">{content.faqHeading}</Typography>
                    </Grid>

                    {content.faq.map((item) => (
                        <Grid item xs={12} key={item.q} sx={{mb: 2}}>
                            <Panel sx={{p: 3, textAlign: 'left'}}>
                                <Typography variant="h4" sx={{mb: 0.5, color: COLOR_SECONDARY}}>{item.q}</Typography>
                                <Typography variant="body2" sx={{color: COLOR_TEXT_MUTED}}>{item.a}</Typography>
                            </Panel>
                        </Grid>
                    ))}

                    <Grid item xs={12} sx={{mt: 4}}>
                        <Button variant='text' href="/" sx={{color: COLOR_TEXT_MUTED, mr: 2}}>{content.backHome}</Button>
                        <Button variant='text' href="/impressum" sx={{color: COLOR_TEXT_MUTED}}>Impressum & {content.privacy}</Button>
                    </Grid>

                </Grid>
            </Grid>
        </div>
    );
}

export default HowItWorks;
